import React from "react";
import { useTheme } from "react-jss";
import { kebabCase } from "../../../utils/lodash";
import useStyles from "./ChoiceListItemRenderer.styles";
import { RendererProps, getRendererProps } from "../BaseRenderer";
import { ChoiceContainer } from "../../../containers/controls/ChoiceContainer";
import { ChoiceModel } from "../../../models/ChoiceModel";
import ChoiceOpenEndRenderer from "./ChoiceOpenEndRenderer";
import { VariableModel } from "../../../models/VariableModel";
import { QuestionType, isChoiceList } from "../../../models/enums/QuestionType";
import { ChoiceRef } from "../../choices/BaseChoice";
import useQuestion from "../../../hooks/useQuestion";
import ChoiceLabelRenderer from "./ChoiceLabelRenderer";
import { ButtonChoice } from "../../shared/ButtonChoice";
import useTranslatable from "../../../hooks/useTranslatable";
import { getSettingString, getSettingNumber } from "../../../models/utilities/settingUtilities";
import { translate } from "../../../models/utilities/translationUtilities";
import { removeHtmlTags } from "../../../utils";
import { hasOpenEnd } from "../../../models/utilities/choiceUtilities";

interface Props extends RendererProps {
    selected?: boolean;
    variable: VariableModel;
    isFirst?: boolean;
    isLast?: boolean;
    name?: string;
    gridRendering?: boolean;
    openEnd?: string;
    onOpenEndChange?(choice: ChoiceModel, text: string): void;
    onOpenEndBlur?(choice: ChoiceModel): void;
    choice?: ChoiceModel;
    horizontal?: boolean;
    controlType?: any;
    disabled?: boolean;
    onAfterSelection?(): void;
    executeUpdateForIdAfterSelection?: string;
}

const Choice = React.forwardRef(function ChoiceImpl(props: Props, ref: ChoiceRef) {
    const {
        choice,
        controlType,
        openEnd,
        isFirst,
        isLast,
        disabled,
        selected,
        variable,
        onOpenEndBlur,
        onOpenEndChange,
        language,
    } = props;

    const theme = useTheme();
    const classes = useStyles({ theme, selected });

    return (
        <>
            <div className={classes.choiceContainer}>
                <ChoiceContainer
                    {...getRendererProps(props)}
                    choiceRef={ref}
                    choice={choice}
                    controlType={controlType}
                    openEnd={openEnd}
                    isFirst={isFirst}
                    isLast={isLast}
                    isDisabled={disabled}
                    isSelected={selected}
                    variable={variable}
                />
            </div>
            <ChoiceOpenEndRenderer
                {...getRendererProps(props)}
                choice={choice}
                isSelected={selected}
                name={choice.elementId}
                label={removeHtmlTags(translate(choice, "TEXT", language))}
                value={openEnd}
                onOpenEndBlur={onOpenEndBlur}
                onOpenEndChange={onOpenEndChange}
                size={getSettingString(choice, "OpenEndSize", "Full")}
                lines={getSettingNumber(choice, "OpenEndLines", 2)}
            />
        </>
    );
});

export default function ChoiceListItemRenderer(props: Props) {
    const {
        choice,
        controlType,
        isFirst,
        isLast,
        disabled,
        openEnd,
        onOpenEndBlur,
        onOpenEndChange,
        gridRendering,
        selected,
        variable,
        horizontal,
        onAfterSelection,
    } = props;
    const choiceRef: ChoiceRef = React.useRef();
    const question = useQuestion();
    const theme = useTheme();
    const classes = useStyles({ theme, selected, horizontal });
    const choiceLabel = removeHtmlTags(useTranslatable(choice, "TEXT"));

    if (!choice.value) {
        return (
            <div
                id={`${choice.elementId}_INPUT`}
                className={classes.choiceHeader}
                aria-labelledby={`${choice.elementId}_LABEL`}
            >
                <ChoiceLabelRenderer choice={choice} gridRendering={gridRendering} />
            </div>
        );
    }

    if (question.type === QuestionType.ImageSelector) {
        return <Choice ref={choiceRef} {...props} />;
    }

    const type = isChoiceList(question.type) ? question.type : QuestionType.RadioButton;

    function onSelect() {
        if (choiceRef.current) {
            choiceRef.current.select();

            if (onAfterSelection) {
                onAfterSelection();
            }
        }
    }

    const openEndSize = getSettingString(choice, "OpenEndSize", "Medium");
    const carrouselChoiceBackgound = getSettingString(choice, "Color");

    return (
        <ButtonChoice
            id={`${choice.elementId}_INPUT`}
            choiceElementId={`${choice.elementId}`}
            type={type === QuestionType.RadioButton ? "radio" : "checkbox"}
            label={choiceLabel}
            onSelect={onSelect}
            hasOpenEnd={hasOpenEnd(choice)}
            openEndSize={openEndSize}
            selected={selected}
            className={kebabCase(QuestionType[type])}
            horizontal={horizontal}
            renderChoice={() => (
                <ChoiceContainer
                    {...getRendererProps(props)}
                    choiceRef={choiceRef}
                    choice={choice}
                    controlType={controlType}
                    openEnd={openEnd}
                    isFirst={isFirst}
                    isLast={isLast}
                    isDisabled={disabled}
                    isSelected={selected}
                    variable={variable}
                />
            )}
            renderAfter={() => (
                <ChoiceOpenEndRenderer
                    {...getRendererProps(props)}
                    choice={choice}
                    isSelected={selected}
                    name={choice.elementId}
                    label={choiceLabel}
                    value={openEnd}
                    onOpenEndBlur={onOpenEndBlur}
                    onOpenEndChange={onOpenEndChange}
                    size={openEndSize}
                    lines={getSettingNumber(choice, "OpenEndLines", 2)}
                />
            )}
            carrouselChoiceBackgound={carrouselChoiceBackgound}
            variable={variable}
        />
    );
}
